/**
 * Renders the vertical stack of platforms for a level.
 *
 * Every platform is an asset key, so Prompt 10's SVGs replace the placeholders
 * with no change here.
 */
import Phaser from 'phaser';
import { artScale } from '../../art/artScale';
import { ensurePlaceholderTexture } from '../../art/placeholders';
import { platformY } from '../../systems/tower';
import type { LevelConfig, PlatformSpec, PlatformTheme } from '../../types';

export const PLATFORM_VARIANTS = 3;
export const PLATFORM_DEPTH = 10;

/** The goal art is 320x200 with the floe at the bottom; Prompt 10 refines this. */
export const GOAL_ORIGIN_Y = 0.82;

/** Asset key for one platform, honouring per-platform themes on mixed levels. */
export function platformKey(platform: PlatformSpec, levelTheme: PlatformTheme): string {
  if (platform.kind === 'start') return 'platforms/start';
  if (platform.kind === 'goal') return 'platforms/goal';
  const theme = platform.theme ?? levelTheme;
  const variant = (platform.index % PLATFORM_VARIANTS) + 1;
  return `platforms/${theme}/platform_${variant}`;
}

/** Every platform key a level will ask for. */
export function levelPlatformKeys(config: LevelConfig): string[] {
  return config.platforms.map((platform) => platformKey(platform, config.platformTheme));
}

export class TowerView {
  private readonly images: Phaser.GameObjects.Image[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly config: LevelConfig,
  ) {
    for (const platform of config.platforms) {
      this.images.push(this.createPlatform(platform));
    }
  }

  imageAt(index: number): Phaser.GameObjects.Image | undefined {
    return this.images[index];
  }

  private createPlatform(platform: PlatformSpec): Phaser.GameObjects.Image {
    const key = platformKey(platform, this.config.platformTheme);
    ensurePlaceholderTexture(this.scene, key);

    const y = platformY(platform.index);
    const image = this.scene.add
      .image(platform.x, y, key)
      .setDepth(PLATFORM_DEPTH)
      .setOrigin(0.5, platform.kind === 'goal' ? GOAL_ORIGIN_Y : 0.5)
      // Platform art is rasterised above its authored size; this puts it back.
      .setScale(artScale(this.scene, key));

    // Levels 31-40 are glowing floes rather than a separate asset set.
    if (this.config.platformGlow && platform.kind !== 'goal') {
      image.setTint(0xbfe9ff);
      this.scene.tweens.add({
        targets: image,
        alpha: { from: 0.82, to: 1 },
        duration: 1600 + (platform.index % 5) * 180,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    return image;
  }
}
